/* ==========================================================================
   custom.js — Club de las Fundas
   --------------------------------------------------------------------------
   Archivo NUESTRO, igual que custom.css: no forma parte de Woodstock y
   sobrevive a una actualizacion del tema.

   Regla del proyecto: lo que se pueda resolver aqui, que no toque un archivo
   del tema. Ver docs/figma-preparacion/12-estado-y-siguiente-paso.md.
   ========================================================================== */

/* --------------------------------------------------------------------------
   <variant-mixed> · selector de variantes hibrido            (bloque 5, §10)
   --------------------------------------------------------------------------
   El Figma pide las dos cosas a la vez en la ficha de producto:
     COLOR                  -> 3 swatches circulares de 28 px
     MODELO DE DISPOSITIVO  -> un select de 520 x 48

   Woodstock solo sabe hacer una de las dos. En main-product-variant-picker
   los swatches viven encerrados en la rama `picker_type == 'button'`
   (<variant-radios>, que lee input[type=radio]:checked) y los desplegables en
   la rama `dropdown` (<variant-selects>, que lee select). Ninguno de los dos
   lee ambos controles, asi que mezclarlos sin mas devolveria una lista de
   opciones incompleta y el producto no encontraria su variante.

   Este elemento lee los dos tipos de control en una sola consulta. El orden
   que devuelve querySelectorAll es orden de documento, que aqui coincide con
   el orden de las opciones porque el snippet pinta cada opcion en su propio
   bloque dentro del bucle. Eso es justo lo que espera selectedOptionValues.

   Es un superconjunto de <variant-radios>: con solo radios se comporta
   exactamente igual, por eso se puede sustituir sin condicionar por ajuste.
   -------------------------------------------------------------------------- */
if (typeof VariantSelects !== 'undefined' && !customElements.get('variant-mixed')) {
  class VariantMixed extends VariantSelects {
    connectedCallback() {
      this.loadBackgroundColorSwatches();
    }

    get selectedOptionValues() {
      return Array.from(
        this.querySelectorAll('select, input[type="radio"]:checked')
      ).map((el) =>
        el.tagName === 'SELECT'
          ? el.options[el.selectedIndex].dataset.optionValueId
          : el.dataset.optionValueId
      );
    }

    /* Copiada de VariantRadios. Sin esto los swatches que usan imagen de
       fondo no llegan a cargarse nunca, porque quien dispara
       loadColorSwatches() es el connectedCallback de aquel elemento. */
    loadBackgroundColorSwatches() {
      if (
        !window.loadedBackgroundColorSwatches &&
        this.querySelectorAll('.product-form__input__radio-label--color').length > 0
      ) {
        loadColorSwatches();
      }
    }
  }

  customElements.define('variant-mixed', VariantMixed);
}

/* --------------------------------------------------------------------------
   Ficha · galeria por modelo y "Selecciona tu modelo"   (cliente, 2026-10-27)
   --------------------------------------------------------------------------
   Peticion del cliente. Ver docs/09-ficha-producto-ajustes-cliente.md.

   1) GALERIA: al elegir modelo solo se ven las fotos de ESE modelo mas las
      generales del producto. Phonecases3D nombra las imagenes asi:

        <DISENO>-<MODELO>.webp           foto del modelo (ligada a la variante)
        <DISENO>-<MODELO>-EXTRA-2.webp   otra vista del mismo modelo (suelta)
        <DISENO>-<MODELO>_<uuid>.webp    duplicado (hoy solo en PC3DM97)
        <DISENO>-ALL-1.webp / -ALL-2     generales: fotos de ambiente

      y el SKU de la variante es exactamente <DISENO>-<MODELO> (comprobado en
      las 4.036 variantes con foto del catalogo: 0 discrepancias). La clave de
      filtrado es el SKU, sin tabla de modelos que mantener.

      Respaldos:
        · fotos que no siguen la convencion (screen-protector): no se filtra.
        · modelo sin foto propia en un producto sin generales (14 casos hoy):
          se deja la portada; nunca una galeria vacia.

      POR QUE EN JS Y NO EN LIQUID (product-media-gallery.liquid): el estado
      "sin modelo elegido" del punto 2 solo existe en el navegador, y en Liquid
      no se distingue "acaba de entrar" de "ha elegido el primer modelo"
      (main-product.liquid:24-29 tiene que esquivar lo mismo). Y asi no se toca
      ningun archivo de Woodstock.

      Se oculta con la clase `hidden` del tema a proposito: el lightbox
      (media-gallery.js, isLightboxOpenerVisible) y el slider (initPages, que
      solo cuenta diapositivas con ancho) ya la respetan.

   2) SIN MODELO PRESELECCIONADO: si la URL no trae ?variant=, el select
      arranca en "Selecciona tu modelo" y no se puede anadir al carrito hasta
      elegir uno (el input oculto name="id" lleva la PRIMERA variante: sin el
      guardian se compraria un modelo que nadie eligio). Con ?variant= se
      respeta: es lo que llega desde el menu "Comprar por Movil" —Shopify anade
      la variante a las tarjetas de una coleccion filtrada por modelo— y ahi el
      cliente ya ha elegido.

      Tras elegir, no hay que deshacer nada: product-info.js vuelve a pintar el
      selector entero desde el servidor y el aviso desaparece solo.
   -------------------------------------------------------------------------- */
(() => {
  const FICHA = 'product-info[id^="MainProduct-"]';
  const TEXTO_SIN_MODELO = 'Selecciona tu modelo';
  const UUID = /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  const urlTraeModelo = () => new URLSearchParams(window.location.search).has('variant');

  /* Nombre de archivo de la foto de un <li> del visor o de las miniaturas. */
  const nombre = (li) => {
    const url =
      li.querySelector('img')?.getAttribute('src') ||
      li.querySelector('a[href]')?.getAttribute('href') ||
      '';
    try {
      return decodeURIComponent(url.split('?')[0].split('/').pop() || '');
    } catch (e) {
      return '';
    }
  };

  /* "ATBI6-IP11-EXTRA-2_<uuid>.webp" -> "ATBI6-IP11" */
  const clave = (archivo) =>
    archivo.replace(/\.[a-z0-9]+$/i, '').replace(UUID, '').split('-EXTRA-')[0].toUpperCase();

  const esGeneral = (archivo) => /-ALL-\d+/i.test(archivo);

  /* data-media-id / data-target valen "<id de seccion>-<id del medio>". */
  const idMedio = (li) => (li.dataset.mediaId || li.dataset.target || '').split('-').pop();

  /* Solo se destapa lo que tapamos nosotros, por si el tema oculta algo por
     su cuenta (su agrupacion por metacampo usa la misma clase). */
  const ocultar = (el, si) => {
    if (si && !el.classList.contains('hidden')) {
      el.classList.add('hidden');
      el.setAttribute('data-cdlf-oculta', '');
    } else if (!si && el.hasAttribute('data-cdlf-oculta')) {
      el.classList.remove('hidden');
      el.removeAttribute('data-cdlf-oculta');
    }
  };

  /* `variante` hace falta siempre (su SKU dice si el producto sigue la
     convencion de nombres); `conModelo` dice si el cliente ya ha elegido.
     Sin modelo, el JSON trae la primera variante, que vale igual para eso. */
  const aplicar = (galeria, variante, conModelo) => {
    const visor = Array.from(galeria.querySelectorAll('[id^="Slider-Gallery"] > [data-media-id]'));
    if (!visor.length) return;
    const miniaturas = Array.from(galeria.querySelectorAll('[id^="Slider-Thumbnails"] > [data-target]'));
    const todos = visor.concat(miniaturas);

    /* La portada es lo que el servidor pinta primero al cargar. Se apunta en
       el primer pase porque despues el tema reordena: setActiveMedia con
       prepend sube la foto del modelo elegido al principio de la lista. */
    if (!galeria.dataset.cdlfPortada) galeria.dataset.cdlfPortada = idMedio(visor[0]);
    const portada = galeria.dataset.cdlfPortada;

    const sku = (variante?.sku || '').toUpperCase();
    const diseno = sku.slice(0, sku.lastIndexOf('-') + 1); // "ATBI6-"
    const fotoVariante = String(variante?.featured_media?.id || '');
    const filtrable = diseno !== '' && todos.some((li) => nombre(li).toUpperCase().startsWith(diseno));

    let seVe;
    if (!filtrable) {
      seVe = () => true;
    } else if (!conModelo) {
      seVe = (archivo, id) => id === portada || esGeneral(archivo);
    } else {
      seVe = (archivo, id) => esGeneral(archivo) || clave(archivo) === sku || id === fotoVariante;
    }
    if (!visor.some((li) => seVe(nombre(li), idMedio(li)))) {
      seVe = (archivo, id) => id === portada;
    }

    todos.forEach((li) => ocultar(li, !seVe(nombre(li), idMedio(li))));

    const visibles = visor.filter((li) => !li.classList.contains('hidden'));

    /* Si la foto activa ha quedado oculta se activa la primera visible, CON
       prepend: con hide_variants el tema esconde toda foto de variante que no
       sea :first-child (section-main-product.css), asi que hay que subirla.
       En el primer pase <media-gallery> aun no esta definido (custom.js corre
       antes que media-gallery.js); lo resuelve el pase de DOMContentLoaded. */
    const activa = visor.find((li) => li.classList.contains('is-active'));
    if (
      (!activa || activa.classList.contains('hidden')) &&
      visibles.length &&
      typeof galeria.setActiveMedia === 'function'
    ) {
      galeria.setActiveMedia(visibles[0].dataset.mediaId, true, false);
    }

    galeria.elements?.viewer?.resetPages?.();
    galeria.elements?.thumbnails?.resetPages?.();

    /* Con una sola foto, una tira de una miniatura no aporta nada. */
    const tira = galeria.querySelector('[id^="GalleryThumbnails"]');
    if (tira) ocultar(tira, visibles.length <= 1);

    galeria.dataset.cdlfGaleria = 'lista';
  };

  const aplicarEnSeccion = (seccion, variante, conModelo) => {
    document
      .querySelectorAll('[id^="MediaGallery-' + seccion + '"]')
      .forEach((galeria) => aplicar(galeria, variante, conModelo));
  };

  const prepararSelect = (ficha) => {
    if (urlTraeModelo()) return;
    /* Hoy la unica opcion de los productos es el modelo. Si algun dia hubiera
       dos selects, solo el primero arrancaria vacio. */
    const select = ficha.querySelector('variant-mixed select.select__select, variant-selects select.select__select');
    if (!select || select.hasAttribute('data-cdlf-sin-modelo') || select.options.length < 2) return;

    /* disabled + hidden: se ve en el select cerrado pero no se puede volver
       a elegir desde la lista. */
    const aviso = new Option(TEXTO_SIN_MODELO, '', true, true);
    aviso.disabled = true;
    aviso.hidden = true;
    select.add(aviso, 0);
    select.selectedIndex = 0;
    select.required = true;
    select.setAttribute('data-cdlf-sin-modelo', '');
  };

  const iniciar = () => {
    document.querySelectorAll(FICHA).forEach((ficha) => {
      prepararSelect(ficha);
      let variante = null;
      try {
        variante = JSON.parse(ficha.querySelector('[data-selected-variant]')?.textContent || 'null');
      } catch (e) {
        variante = null;
      }
      aplicarEnSeccion(ficha.dataset.section, variante, urlTraeModelo());
    });
  };

  /* product-info.js lo emite al terminar de cambiar de variante, despues de
     su propio updateMedia (que ya ha subido la foto del modelo nuevo). */
  document.addEventListener('afterVariantChanged', (e) => {
    const { sectionId, variant } = e.detail || {};
    if (!sectionId || !variant || !document.getElementById('MainProduct-' + sectionId)) return;
    aplicarEnSeccion(sectionId, variant, true);
  });

  /* Guardian de submit: sin modelo no se anade al carrito. Mismo patron que
     el de personalizacion (mas abajo): en CAPTURA para correr antes que
     product-form.js, y reportValidity() para el globo nativo, que sale
     aunque el form lleve novalidate. Va registrado ANTES que aquel, asi que
     primero se pide el modelo, que es lo que esta mas arriba en la ficha. */
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || form.tagName !== 'FORM') return;

      /* 🪤 getAttribute('id') y NO form.id: el formulario de compra contiene un
         <input name="id"> (la variante), y en un <form> los controles con
         name tapan las propiedades del mismo nombre -> form.id devuelve ese
         input, no el id del formulario. Asi se colo el primer intento. */
      const idForm = form.getAttribute('id');
      const select = Array.from(document.querySelectorAll('select[data-cdlf-sin-modelo]')).find(
        (s) => (idForm && s.getAttribute('form') === idForm) || form.contains(s)
      );
      if (!select || select.value !== '') return;

      e.preventDefault();
      e.stopImmediatePropagation();
      select.setAttribute('data-cdlf-invalido', '');
      select.focus();
      if (typeof select.reportValidity === 'function') select.reportValidity();
    },
    true
  );

  document.addEventListener('change', (e) => {
    const select = e.target;
    if (select.matches?.('select[data-cdlf-invalido]') && select.value !== '') {
      select.removeAttribute('data-cdlf-invalido');
    }
  });

  document.addEventListener('DOMContentLoaded', iniciar);
  document.addEventListener('shopify:section:load', iniciar);
  if (document.readyState !== 'loading') iniciar();
})();

/* --------------------------------------------------------------------------
   Carrusel de dispositivos · flechas y arrastre               (bloque 3, §8)
   --------------------------------------------------------------------------
   El efecto de fijacion se quito el 2026-08-20 (ver custom.css). La fila pasa
   a desplazarse a mano y aqui se le ponen los dos controles:

     · FLECHAS, a la derecha del titular. Decision del usuario (2026-08-20).
       P-11 recomendaba lo contrario ("scroll con peek como senal, sin flechas
       en desktop"), pero seguia ABIERTA y manda el usuario. El peek se queda:
       las dos cosas se complementan.
     · ARRASTRE con raton, que sigue siendo util aunque haya flechas.

   POR QUE SE CONSTRUYEN AQUI Y NO EN LA SECCION: el theme ya sabe hacer esto
   con <slider-component> (critical-global.js), pero engancharlo obligaria a
   editar sections/promotion-cards-pinning.liquid, que es de Woodstock. Regla
   del doc 12: eso vive en archivos nuestros. Se reutilizan sus CLASES
   (.slider-buttons, .slider-button--prev/next) y su marcado de dos <span>, que
   es como el theme dibuja el chevron con bordes girados 45 grados, asi que las
   flechas salen identicas a las de los demas carruseles de la tienda y heredan
   gratis el estado [disabled] (chevron aplanado + opacidad 0,5).

   Los textos de aria-label son los mismos que sirve el theme en es.json para
   sus propios sliders.
   -------------------------------------------------------------------------- */
(() => {
  const SECCION = '[id$="__dispositivos"]';
  const UMBRAL_ARRASTRE = 5;
  const HOLGURA = 2; /* px de tolerancia al redondeo del scroll */

  const rebotar = (fn, ms) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };

  /* Un "paso" = las tarjetas ENTERAS que caben. Sale de medir el DOM, no de
     leer el ajuste de columnas, asi que sigue siendo correcto en cualquier
     breakpoint y con cualquier numero de tarjetas. Con el peek, lo visible es
     4,5 / 2,5 / 1,5 → floor da 4 / 2 / 1, que es justo lo que hay que avanzar
     para no saltarse ninguna. */
  const calcularPaso = (lista) => {
    const items = lista.querySelectorAll('.pcp__item');
    if (items.length < 2) return lista.clientWidth;
    const salto = items[1].offsetLeft - items[0].offsetLeft;
    if (!(Math.abs(salto) > 0.5)) return lista.clientWidth;
    const margen = parseFloat(getComputedStyle(items[0]).marginInlineStart) || 0;
    const enteras = Math.max(1, Math.floor((lista.clientWidth - margen) / Math.abs(salto)));
    return salto * enteras;
  };

  const montarFlechas = (seccion, lista) => {
    const titular = seccion.querySelector('.pcp__heading');
    if (!titular || titular.dataset.flechasListas) return;
    titular.dataset.flechasListas = 'true';

    if (!lista.id) lista.id = 'cdlf-pcp-lista-' + Math.random().toString(36).slice(2, 8);

    /* El titular pasa a ser una fila: texto a la izquierda, flechas a la
       derecha. Mover los nodos NO rompe appear-animate-list: guarda referencias
       a los elementos, no a su posicion en el arbol. */
    const fila = document.createElement('div');
    fila.className = 'cdlf-pcp-titular-fila';
    const texto = document.createElement('div');
    texto.className = 'cdlf-pcp-titular-texto';
    while (titular.firstChild) texto.appendChild(titular.firstChild);
    fila.appendChild(texto);

    const grupo = document.createElement('div');
    grupo.className = 'slider-buttons slider-buttons--box flex align-center cdlf-pcp-flechas';

    const crear = (sentido, etiqueta) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.name = sentido === 'prev' ? 'previous' : 'next';
      b.className =
        'slider-button flex flex-column justify-center align-center por tap-area-icon slider-button--' + sentido;
      b.setAttribute('aria-label', etiqueta);
      b.setAttribute('aria-controls', lista.id);
      b.appendChild(document.createElement('span'));
      b.appendChild(document.createElement('span'));
      grupo.appendChild(b);
      return b;
    };

    const anterior = crear('prev', 'Diapositiva a la izquierda');
    const siguiente = crear('next', 'Diapositiva a la derecha');

    fila.appendChild(grupo);
    titular.appendChild(fila);

    const actualizar = () => {
      const maximo = lista.scrollWidth - lista.clientWidth;
      /* Si no hay nada que desplazar (pocas tarjetas, o pantalla muy ancha
         donde caben todas) el grupo entero se esconde: dos flechas muertas
         son peor que ninguna. */
      grupo.classList.toggle('cdlf-pcp-flechas--ocultas', maximo <= HOLGURA);
      anterior.disabled = lista.scrollLeft <= HOLGURA;
      siguiente.disabled = lista.scrollLeft >= maximo - HOLGURA;
    };

    anterior.addEventListener('click', () =>
      lista.scrollBy({ left: -calcularPaso(lista), behavior: 'smooth' })
    );
    siguiente.addEventListener('click', () =>
      lista.scrollBy({ left: calcularPaso(lista), behavior: 'smooth' })
    );

    lista.addEventListener('scroll', actualizar, { passive: true });
    window.addEventListener('resize', rebotar(actualizar, 200));
    actualizar();
  };

  const enganchar = (lista) => {
    if (lista.dataset.arrastreListo) return;
    lista.dataset.arrastreListo = 'true';

    lista.querySelectorAll('a, img').forEach((el) => el.setAttribute('draggable', 'false'));

    let arrastrando = false;
    let seMovio = false;
    let xInicial = 0;
    let scrollInicial = 0;

    lista.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      arrastrando = true;
      seMovio = false;
      xInicial = e.pageX;
      scrollInicial = lista.scrollLeft;
      /* scroll-behavior: smooth pelearia con el arrastre: cada frame lanzaria
         una animacion nueva y el movimiento saldria a tirones. */
      lista.style.scrollBehavior = 'auto';
      lista.style.cursor = 'grabbing';
    });

    const soltar = () => {
      if (!arrastrando) return;
      arrastrando = false;
      lista.style.scrollBehavior = '';
      /* 'grab', no '': vaciarlo dejaria la fila sin la pista del cursor a
         partir del primer arrastre. */
      lista.style.cursor = 'grab';
      /* Se libera en el siguiente tick para que el click que viene detras del
         mouseup todavia encuentre seMovio en true y quede cancelado. */
      setTimeout(() => { seMovio = false; }, 0);
    };

    lista.addEventListener('mouseleave', soltar);
    window.addEventListener('mouseup', soltar);

    lista.addEventListener('mousemove', (e) => {
      if (!arrastrando) return;
      const dx = e.pageX - xInicial;
      if (!seMovio && Math.abs(dx) < UMBRAL_ARRASTRE) return;
      seMovio = true;
      e.preventDefault();
      lista.scrollLeft = scrollInicial - dx;
    });

    lista.addEventListener('click', (e) => {
      if (!seMovio) return;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    lista.style.cursor = 'grab';
  };

  const iniciar = () => {
    /* El arrastre solo tiene sentido con puntero fino y sin tactil: con dedo y
       con trackpad el desplazamiento ya es nativo. Las flechas, en cambio, se
       montan siempre — en movil las esconde el CSS, no el JS. */
    const conRaton = !('ontouchstart' in window) && window.matchMedia('(pointer: fine)').matches;

    document.querySelectorAll(SECCION).forEach((seccion) => {
      const lista = seccion.querySelector('.pcp__list');
      if (!lista) return;
      montarFlechas(seccion, lista);
      if (conRaton) enganchar(lista);
    });
  };

  document.addEventListener('DOMContentLoaded', iniciar);
  /* El editor de temas rehace la seccion entera al cambiar un ajuste. */
  document.addEventListener('shopify:section:load', iniciar);
  if (document.readyState !== 'loading') iniciar();
})();

/* --------------------------------------------------------------------------
   Campos de personalizacion · espejo, contador y obligatoriedad   (feature)
   --------------------------------------------------------------------------
   snippets/custom-personalization-fields.liquid pinta, solo en productos con
   SKU "C_*", uno o dos campos que el cliente rellena SIN atributo name —no se
   envian solos— y las propiedades de linea ocultas que este codigo mantiene
   en espejo con lo tecleado:

     properties[Nombre|Iniciales|Firma] / properties[Numero]  -> legibles en
                                       carrito y checkout
     properties[_custom_text_1] / properties[_custom_text_2]  -> las lee la
                                       app de Phonecases3D (nombre EXACTO)

   OJO: la segunda es _custom_text_2, NO _custom_number_1. Cambio confirmado
   por Nick el 2026-09-14: su app lee las dos como texto, tambien la del
   numero. Ver docs/productos_reales/email_nick_2026-09-14.md.

   Que campos se pintan, con que etiqueta y con que maxlength lo decide el
   snippet a partir de la base del SKU. Aqui no hay nada especifico de
   producto: este codigo funciona con 1 o 2 campos sin saber cual es cual.

   Cada oculta esta 'disabled' mientras su campo esta vacio, asi que un pedido
   sin personalizar no lleva ninguna propiedad. Con los campos ya obligatorios
   esto es una red de seguridad, no el caso normal.

   POR QUE DELEGACION EN document Y NO UN LISTENER POR CAMPO: asi no hay que
   volver a enganchar nada cuando el editor de temas rehace la seccion, ni
   cuando product-info.js refresca la caja de compra al cambiar de modelo
   (en ese refresco los campos ni se recrean —es una actualizacion dirigida,
   no un innerHTML del formulario— pero si algun dia lo hiciera, esto
   seguiria valiendo sin tocar nada).

   'input'  cubre el tecleo normal (las ocultas se actualizan en vivo).
   'change' cubre el autorrelleno del navegador y el "volver atras", que no
            siempre disparan 'input'; ademas salta al desenfocar el campo,
            que ocurre ANTES del submit al pulsar "Anadir al carrito", asi
            que las ocultas siempre van llenas cuando el tema construye el
            FormData.
   -------------------------------------------------------------------------- */
(() => {
  const soloDigitos = (v) => v.replace(/\D+/g, '');

  const copiar = (campo) => {
    const caja = campo.closest('[data-cdlf-perso]');
    if (!caja) return;

    const clave = campo.dataset.cdlfPersoSrc;
    let valor = campo.value.trim();
    /* El campo del numero se queda solo en digitos ("nº 10", "10.", "  10"
       -> "10"), tanto en la propiedad legible como en la que lee la app. */
    if (campo.hasAttribute('data-cdlf-perso-digits')) valor = soloDigitos(valor);

    caja.querySelectorAll('[data-cdlf-perso-mirror="' + clave + '"]').forEach((oculto) => {
      oculto.value = valor;
      /* Vacio -> deshabilitado -> no se envia. */
      oculto.disabled = valor === '';
    });

    /* Contador "3/12". El maxlength lo pone el snippet segun el producto, asi
       que se lee del propio campo en vez de hardcodearlo aqui. */
    const contador = caja.querySelector('[data-cdlf-perso-count="' + clave + '"]');
    if (contador) contador.textContent = valor.length + '/' + campo.maxLength;

    /* En cuanto el campo deja de estar vacio se quita el aviso de error. */
    if (valor !== '') campo.removeAttribute('data-cdlf-perso-invalid');
  };

  const alTocar = (e) => {
    const campo = e.target.closest('[data-cdlf-perso-src]');
    if (!campo) return;
    /* Al desenfocar el campo del numero se normaliza tambien lo que se ve,
       para que el cliente no lea una cosa y se fabrique otra. */
    if (e.type === 'change' && campo.hasAttribute('data-cdlf-perso-digits')) {
      campo.value = soloDigitos(campo.value.trim());
    }
    copiar(campo);
  };

  document.addEventListener('input', alTocar);
  document.addEventListener('change', alTocar);

  /* ------------------------------------------------------------------------
     Guardian de submit: impide anadir al carrito sin personalizar.
     ------------------------------------------------------------------------
     Lo pidio Nick: hoy se puede pedir un producto C_* con los campos vacios y
     les llega sin datos que imprimir.

     POR QUE NO BASTA EL ATRIBUTO required: el <form> de compra de Woodstock
     lleva `novalidate` (product-sale-box.liquid), que desactiva la validacion
     nativa al enviar. El required se queda como semantica (lectores de
     pantalla) y como marca que lee este guardian.

     POR QUE EN CAPTURA (tercer argumento true): product-form.js engancha su
     handler en el propio <form> (fase de burbuja). Un listener en document en
     fase de CAPTURA corre antes, asi que aqui se puede parar el envio con
     stopImmediatePropagation() y el handler del tema no llega a ejecutarse.

     reportValidity() sobre el INPUT si muestra el globo nativo aunque el form
     tenga novalidate (novalidate solo afecta al envio automatico, no a las
     llamadas explicitas). Aun asi se marca el campo con
     data-cdlf-perso-invalid para que el borde rojo quede visible aunque el
     globo no salga o se cierre.
     ------------------------------------------------------------------------ */
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || form.tagName !== 'FORM') return;

      const caja = form.querySelector('[data-cdlf-perso]');
      if (!caja) return;

      const campos = caja.querySelectorAll('[data-cdlf-perso-src][required]');
      let primerVacio = null;
      campos.forEach((campo) => {
        const vacio = campo.value.trim() === '';
        if (vacio) {
          campo.setAttribute('data-cdlf-perso-invalid', '');
          if (!primerVacio) primerVacio = campo;
        } else {
          campo.removeAttribute('data-cdlf-perso-invalid');
        }
      });

      if (!primerVacio) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      primerVacio.focus();
      if (typeof primerVacio.reportValidity === 'function') primerVacio.reportValidity();
    },
    true
  );
})();
